/**
 * Dato- og tidsinterval-formattering — KANONISK kilde for hele UI'et.
 *
 * Alle datoer og relative tidsintervaller i Cockpit's frontend SKAL formateres
 * via disse helpers, så visningen er konsistent overalt. Reglerne er
 * dokumenteret bindende i `docs/03-ui-design.md` §11.1 (Charlie's konvention,
 * session 29).
 *
 * Dato-format (dansk; månedsnavn uden trailing punktum):
 *   - i dag                         → "I dag"
 *   - i går                         → "I går"
 *   - enkelt dato                   → "5. jun 2026"   (lille md. når dag er med)
 *   - range, samme måned            → "5-20. jun 2026"
 *   - range, samme år, ny måned     → "23. maj - 18. jun 2026"
 *   - range, over årsskifte         → "23. maj 2025 - 18. jun 2026"
 *   - kun måned+år (formatMonthDa)  → "Jun 2026"      (STORT md. uden dag)
 *   - måned+år i CHARTS (formatMonthShortDa) → "Jun '26" (Charlie session 40:
 *     akse-/chart-labels skriver ALTID måned+år kort med '-årstal)
 *
 * Klokkeslæt (Charlie 19-08-2026 — 12-conventions §27):
 *   - ALTID kolon: "09:08" (kort) og "09:08:25" (med sekunder). Aldrig
 *     "09.08.25", som da-DK's egen formattering ellers giver — punktummer
 *     læses som en DATO. Brug `formatTimeDa`, aldrig toLocaleTimeString.
 *
 * Fuld dato i tooltip (Charlie 23-10-2026 — 12-conventions §94): ALLE steder
 * der kan vise en relativ dato ("I dag", "I går", "x siden"), bærer direktivet
 * `v-fulldate="tidspunkt"` på elementet, så ugedag, dato og klokkeslæt står i
 * tooltippen (`formatFullDateTimeDa`). Inde i en tooltip-tekst (v-tip) kan
 * der ikke hænge en ny tooltip, så der skrives datoen konkret
 * (`relative: false` / `formatFullDateTimeDa`). Web-buildet håndhæver det
 * (scripts/check-relative-dates.ts).
 *
 * Udskrifter (PDF/Excel — 12-conventions §27): mens `isPrinting` er sat,
 * skriver formatterne den KONKRETE dato i stedet for "I dag"/"I går"/"x siden".
 * Et gemt dokument skal kunne læses om en måned, og der er "i går" tomt.
 *
 * Tidsinterval (magnitude; relativ-form tilføjer " siden"):
 *   < 90 min        → "63 min"
 *   < 180 min       → "2,4 timer"   (1 decimal)
 *   < 36 timer      → "14 timer"    (heltal)
 *   < 72 timer      → "2,6 dage"    (1 decimal)
 *   < 14 dage       → "9 dage"      (heltal)
 *   < 6 uger        → "5,3 uger"    (1 decimal)
 *   < 3 måneder     → "2,4 mdr."    (1 decimal)
 *   < 12 måneder    → "6 mdr."      (heltal)
 *   < 3 år          → "1,8 år"      (1 decimal)
 *   ≥ 3 år          → "5 år"        (heltal)
 */

import { isPrinting } from './print-mode';

/**
 * Varighed (hvor længe noget tog eller mangler) bor i @cockpit/shared, så
 * server og web skriver ens (03-ui-design §11.2, [D-523]): kort form "43 m"
 * med v-duration på elementet, der giver "43 minutter og 3 sekunder" i
 * tooltippen.
 */
export { formatDurationDa, formatDurationFullDa } from '@cockpit/shared';

/**
 * Danske korte månedsnavne — uden trailing punktum.
 *
 * Konvention (session 35): når måneden vises SAMMEN med en dag (fuld dato,
 * fx "1. aug 2026") skrives måneden med LILLE forbogstav. Når måneden vises
 * UDEN dag (kun måned+år, fx "Aug 2026" — typisk grafer/tabeller) skrives den
 * med STORT forbogstav. Derfor er basis-listen lowercase, og kun `formatMonthDa`
 * kapitaliserer.
 */
const MONTHS_DA = [
  'jan',
  'feb',
  'mar',
  'apr',
  'maj',
  'jun',
  'jul',
  'aug',
  'sep',
  'okt',
  'nov',
  'dec',
] as const;

interface Ymd {
  y: number;
  /** 0-baseret måned. */
  m: number;
  d: number;
}

/** Parser 'YYYY-MM-DD' (ignorerer evt. tid-suffix). Null hvis ugyldig. */
function parseIso(iso: string): Ymd | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) };
}

/** Lokal dato → 'YYYY-MM-DD'. */
function localYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Normaliser et YmD til 'YYYY-MM-DD' til sammenligning. */
function ymdKey(p: Ymd): string {
  return `${p.y}-${String(p.m + 1).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}

/** "5. jun." (dag + måned, uden år). */
function dayMonth(p: Ymd): string {
  return `${p.d}. ${MONTHS_DA[p.m]}`;
}

/**
 * Formater en enkelt ISO-dato ('YYYY-MM-DD') til dansk visning med "I dag"/
 * "I går"-genveje. Ugyldig input returneres uændret.
 * `relative=false` slår genvejene fra og giver altid den konkrete dato —
 * til kontekster hvor datoen indgår i en sætning ("Forventet pr. 6. jun 2026",
 * session 31) eller skal kunne læses i et screenshot/print senere.
 */
export function formatDateDa(iso: string, relative = true): string {
  const p = parseIso(iso);
  if (!p) return iso;
  // §27: i en udskrift er "I dag" ubrugelig — dokumentet læses senere.
  if (relative && !isPrinting.value) {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const key = ymdKey(p);
    if (key === localYmd(today)) return 'I dag';
    if (key === localYmd(yesterday)) return 'I går';
  }
  return `${p.d}. ${MONTHS_DA[p.m]} ${p.y}`;
}

const MONTHS_LONG_DA = [
  'januar',
  'februar',
  'marts',
  'april',
  'maj',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'december',
] as const;
const WEEKDAYS_DA = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'] as const;

/** Husets kalenderdato for et tidspunkt: 'YYYY-MM-DD' i Europe/Copenhagen. */
const CPH_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Copenhagen',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Den FULDE dato til tooltippen bag en relativ dato (Charlie 23-10-2026,
 * 03-ui-design §11.1, 12-conventions §94): "Torsdag 22. oktober 2026 kl. 14:53".
 *
 * Viser UI'et "I dag", "I går" eller "3 timer siden", skal læseren altid kunne
 * holde musen over og se ugedag, dato og klokkeslæt. Den bruges af direktivet
 * `v-fulldate` (directives/tip.ts), ikke direkte i templates.
 *
 * Værdien kan være epoch-ms, en Date, et ISO-tidspunkt eller en ren dato
 * ('YYYY-MM-DD'). En ren dato har intet klokkeslæt, og så udelades det frem for
 * at opfinde "kl. 00:00" ([D-107]: ærlighed i tal). Tidszonen er husets.
 */
export function formatFullDateTimeDa(value: Date | number | string | null | undefined): string {
  if (value == null || value === '') return '';
  const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = dateOnly ? null : value instanceof Date ? value : new Date(value);
  if (d && Number.isNaN(d.getTime())) return '';
  const p = parseIso(d ? CPH_DATE.format(d) : (value as string));
  if (!p) return '';
  const weekday = WEEKDAYS_DA[new Date(Date.UTC(p.y, p.m, p.d)).getUTCDay()]!;
  const date = `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${p.d}. ${MONTHS_LONG_DA[p.m]} ${p.y}`;
  return d ? `${date} kl. ${formatTimeDa(d)}` : date;
}

/**
 * Dato + klokkeslæt: "9. jul 2026 · 09:00" (Europe/Copenhagen, aldrig
 * relativ). isoTs er event-tidspunktet (ISO UTC); isoDate er den CPH-daterede
 * YYYY-MM-DD serveren allerede har afledt — datoen genberegnes IKKE fra
 * timestampen, så label og dato-filterkolonne aldrig kan divergere.
 */
export function formatDateTimeDa(isoTs: string, isoDate: string): string {
  return `${formatDateDa(isoDate, false)} · ${formatTimeDa(isoTs, { seconds: false })}`;
}

/**
 * Klokkeslæt i Europe/Copenhagen med KOLON: "09:08" (default) eller
 * "09:08:25" (`seconds: true`) — 12-conventions §27.
 *
 * ⚠ IKKE `toLocaleTimeString('da-DK')`: dansk locale skiller time og minut med
 * PUNKTUM ("09.08.25"), og det læses som en dato. Formatteringen sker derfor
 * med en locale der garanteret giver kolon (en-GB) + `hourCycle: 'h23'`, så
 * midnat er "00:00" og ikke "24:00". Tidszonen er husets, ikke browserens.
 */
export function formatTimeDa(
  value: Date | number | string,
  opts: { seconds?: boolean } = {},
): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    ...(opts.seconds ? { second: '2-digit' } : {}),
    hourCycle: 'h23',
    timeZone: 'Europe/Copenhagen',
  }).format(d);
}

/**
 * Formater et dato-interval [startIso, endIso] efter den danske range-konvention.
 * Hvis start === slut returneres den enkelte dato (med I dag/I går-genvej).
 */
export function formatDateRangeDa(startIso: string, endIso: string): string {
  const s = parseIso(startIso);
  const e = parseIso(endIso);
  if (!s || !e) return `${startIso} - ${endIso}`;
  if (ymdKey(s) === ymdKey(e)) return formatDateDa(startIso);
  // Samme år + samme måned → "5-20. jun. 2026".
  if (s.y === e.y && s.m === e.m) {
    return `${s.d}-${e.d}. ${MONTHS_DA[e.m]} ${e.y}`;
  }
  // Samme år, forskellig måned → "23. maj - 18. jun. 2026".
  if (s.y === e.y) {
    return `${dayMonth(s)} - ${dayMonth(e)} ${e.y}`;
  }
  // Over årsskifte → "23. maj 2025 - 18. jun. 2026".
  return `${dayMonth(s)} ${s.y} - ${dayMonth(e)} ${e.y}`;
}

/**
 * Som `formatDateRangeDa`, men slutdatoen må mangle: en LØBENDE periode uden
 * planlagt afslutning skrives "1. jul 2026 og frem" ([D-264], fx kampagner
 * som outlet og oprydningsudsalget). Brug den overalt hvor en periodes slut
 * kan være ukendt, så visningen aldrig falder tilbage på rå ISO.
 */
export function formatOpenRangeDa(startIso: string, endIso: string | null | undefined): string {
  if (!endIso) {
    const s = parseIso(startIso);
    return s ? `${dayMonth(s)} ${s.y} og frem` : `${startIso} og frem`;
  }
  return formatDateRangeDa(startIso, endIso);
}

/** Formater en måned ('YYYY-MM') → "Jun 2026" (løbende tekst/titler/tooltips). */
export function formatMonthDa(yyyymm: string): string {
  const match = /^(\d{4})-(\d{2})/.exec(yyyymm);
  if (!match) return yyyymm;
  const monthIdx = Number(match[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return yyyymm;
  // Kun måned+år (uden dag) → STORT forbogstav (konvention session 35).
  const m = MONTHS_DA[monthIdx]!;
  return `${m.charAt(0).toUpperCase()}${m.slice(1)} ${match[1]}`;
}

/** '1-6 aug 2026' — delvis periode deklarerer sit interval i KORT form (fast
 *  formateringsregel, Charlie 06-08-2026; 12-conventions §11). Med dag foran
 *  skrives måneden med LILLE forbogstav (dansk retskrivning — stort er kun
 *  formatMonthDa's alene-stående måned+år). */
export function formatDayRangeMonthDa(yyyymm: string, fromDay: number, toDay: number): string {
  const match = /^(\d{4})-(\d{2})/.exec(yyyymm);
  if (!match) return yyyymm;
  const monthIdx = Number(match[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return yyyymm;
  return `${fromDay}-${toDay} ${MONTHS_DA[monthIdx]!} ${match[1]}`;
}

/**
 * Formater en måned ('YYYY-MM') → "Jun '26" — KANONISK for CHARTS (akse-labels
 * o.l., Charlie session 40): måned+år i grafer skrives altid kort med '-årstal.
 * Løbende tekst/sektions-titler/tooltips bruger fortsat formatMonthDa.
 */
export function formatMonthShortDa(yyyymm: string): string {
  const match = /^(\d{4})-(\d{2})/.exec(yyyymm);
  if (!match) return yyyymm;
  const monthIdx = Number(match[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return yyyymm;
  const m = MONTHS_DA[monthIdx]!;
  return `${m.charAt(0).toUpperCase()}${m.slice(1)} '${match[1]!.slice(2)}`;
}

// ---------------------------------------------------------------------------
// ISO-uger (Charlie 19-08-2026: Uge som granularitet på dashboards)
// ---------------------------------------------------------------------------

/**
 * ISO-8601-uger, altså mandag til søndag, med uge 1 som den uge der indeholder
 * årets første torsdag. Det er den uge-optælling danskere bruger, og den samme
 * som Business Central og kalenderen i Outlook.
 *
 * ⚠ UGE-ÅRET ER IKKE KALENDERÅRET. De sidste dage i december kan høre til uge 1
 * i det følgende år (og 1.-3. januar til uge 52/53 i det forrige). Nøglen bærer
 * derfor UGE-året, ikke `dato.slice(0, 4)` — ellers ville 29-12-2025 og
 * 02-01-2025 lande i samme bucket, og en enkelt uge blive talt 2 gange.
 *
 * Nøgleformatet er 'YYYY-Wnn' med førende nul, så nøgler kan sorteres som
 * almindelige strenge (samme princip som '2026-08' og '2026-Q3').
 */
const WEEK_KEY = /^(\d{4})-W(\d{2})$/;
const DAY_MS = 86_400_000;

/** ISO-uge-nøglen for en dato: '2026-08-19' → '2026-W34'. */
export function isoWeekKeyOf(isoDate: string): string {
  const d = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  // Torsdagen i samme uge afgør BÅDE ugenummeret og uge-året (ISO-8601).
  const weekday = d.getUTCDay() || 7; // mandag = 1 … søndag = 7
  d.setUTCDate(d.getUTCDate() + 4 - weekday);
  const weekYear = d.getUTCFullYear();
  const jan1 = Date.UTC(weekYear, 0, 1);
  const week = Math.floor((d.getTime() - jan1) / DAY_MS / 7) + 1;
  return `${weekYear}-W${String(week).padStart(2, '0')}`;
}

/** Mandagen i en uge-nøgle: '2026-W34' → '2026-08-17'. */
export function isoWeekStart(key: string): string {
  const m = WEEK_KEY.exec(key);
  if (!m) return key;
  // 4. januar ligger ALTID i uge 1, så dens mandag er ugekalenderens nulpunkt.
  const jan4 = new Date(Date.UTC(Number(m[1]), 0, 4));
  const weekday = jan4.getUTCDay() || 7;
  const monday = new Date(jan4.getTime() + (1 - weekday + (Number(m[2]) - 1) * 7) * DAY_MS);
  return monday.toISOString().slice(0, 10);
}

/** Søndagen i en uge-nøgle: '2026-W34' → '2026-08-23'. */
export function isoWeekEnd(key: string): string {
  const start = isoWeekStart(key);
  if (start === key) return key;
  return new Date(Date.parse(`${start}T00:00:00Z`) + 6 * DAY_MS).toISOString().slice(0, 10);
}

/**
 * Uge-nøgle → kort chart-label: '2026-W34' → "U34 '26". Samme form som
 * `formatMonthShortDa` ("Aug '26"), så en akse ser ens ud uanset granularitet.
 * Årstallet står med, fordi et ugenummer alene er tvetydigt over et årsskifte.
 */
export function formatIsoWeekShortDa(key: string): string {
  const m = WEEK_KEY.exec(key);
  if (!m) return key;
  return `U${Number(m[2])} '${m[1]!.slice(2)}`;
}

/**
 * Uge-nøgle → fuld tekst med datoerne: '2026-W34' → "Uge 34, 17-23. aug 2026".
 * Til tooltips og steder hvor læseren skal kunne se hvilke dage ugen dækker
 * uden at slå det op.
 */
export function formatIsoWeekLongDa(key: string): string {
  const m = WEEK_KEY.exec(key);
  if (!m) return key;
  return `Uge ${Number(m[2])}, ${formatDateRangeDa(isoWeekStart(key), isoWeekEnd(key))}`;
}

// ---------------------------------------------------------------------------
// Tidsintervaller
// ---------------------------------------------------------------------------

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30.44 * DAY; // gennemsnitlig kalendermåned (365,25 / 12)
const YEAR = 365.25 * DAY;

/** Tal med præcis 1 decimal, dansk komma (2.4 → "2,4"). */
function dec1(n: number): string {
  return n.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Formater et tidsinterval (ms) som magnitude efter den bindende skala.
 * Tilføjer IKKE " siden" — brug `formatRelativeAgoDa` til relativ-form.
 */
export function formatIntervalDa(ms: number): string {
  const t = Math.max(0, ms);
  if (t < 90 * MIN) return `${Math.round(t / MIN)} min`;
  if (t < 180 * MIN) return `${dec1(t / HOUR)} timer`;
  if (t < 36 * HOUR) return `${Math.round(t / HOUR)} timer`;
  if (t < 72 * HOUR) return `${dec1(t / DAY)} dage`;
  if (t < 14 * DAY) return `${Math.round(t / DAY)} dage`;
  if (t < 6 * WEEK) return `${dec1(t / WEEK)} uger`;
  if (t < 3 * MONTH) return `${dec1(t / MONTH)} mdr.`;
  if (t < 12 * MONTH) return `${Math.round(t / MONTH)} mdr.`;
  if (t < 3 * YEAR) return `${dec1(t / YEAR)} år`;
  return `${Math.round(t / YEAR)} år`;
}

/**
 * Relativ "tid siden" — magnitude + " siden" (fx "63 min siden").
 *
 * `atMs` er tidspunktet der måles fra (epoch-ms). Det bruges KUN i udskrifter
 * (§27): på papir står der den konkrete dato i stedet for "63 min siden", som
 * ingen kan omregne bagefter. Kald derfor ALTID med `atMs`, når værdien kan
 * havne i en PDF — uden den falder udskriften tilbage til den relative form,
 * fordi tidspunktet så ikke kendes.
 */
export function formatRelativeAgoDa(ms: number, atMs?: number): string {
  if (isPrinting.value && atMs !== undefined) {
    return formatEpochDateTimeDa(atMs, { relative: false, seconds: false });
  }
  if (ms < 1000) return 'lige nu';
  return `${formatIntervalDa(ms)} siden`;
}

/**
 * Epoch-ms → dansk dato + klokkeslæt, efter samme konvention som
 * `formatDateTimeDa` ([D-202]).
 *
 *   relative=true  → "I dag · 11:15:47"   (kompakt, til en lang liste)
 *   relative=false → "18. aug 2026 · 11:15:47"
 *
 * Findes fordi systemloggen arbejder i epoch-ms, mens `formatDateTimeDa` tager
 * ISO-strenge. Uden den blev datoen formateret i hånden i viewet og fulgte ikke
 * appens konvention — det var netop dét der gik galt første gang.
 */
export function formatEpochDateTimeDa(
  ms: number,
  opts: { relative?: boolean; seconds?: boolean } = {},
): string {
  const d = new Date(ms);
  const time = formatTimeDa(d, { seconds: opts.seconds !== false });
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  // §27: formatDateDa slår selv den relative genvej fra under print.
  return `${formatDateDa(iso, opts.relative !== false)} · ${time}`;
}
